/**
 * @deprecated in effect — kept for import-path compatibility.
 *
 * The Google Maps JS API is no longer loaded (the Places page runs on
 * react-leaflet + CARTO tiles + Nominatim/Overpass now). Leaflet is a static
 * ES module import, so there is no async load to wait on: this hook resolves
 * to `'ready'` immediately on mount.
 *
 * The `MapsStatus` union is preserved so existing callers keep type-checking
 * (`no-key` and `error` can never fire from the real hook but stay in the
 * union so `switch` fallbacks remain exhaustive and test mocks can still
 * assert on those states).
 */
import { useEffect, useState } from 'react';

/** Loading lifecycle. Historically drove the Google Maps JS API load. */
export type MapsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'no-key';

interface UseGoogleMapsOptions {
  /**
   * When `false`, the hook stays `'idle'` — historically kept the ~120 kB
   * Google script off the critical path until the caller opted in. Now
   * purely cosmetic (no script to load) but retained for API parity.
   */
  enabled?: boolean;
}

/**
 * Report readiness of the map layer. Under the Leaflet migration this is a
 * synchronous constant: `'ready'` when enabled, `'idle'` otherwise.
 */
export function useGoogleMaps(options: UseGoogleMapsOptions = {}): {
  status: MapsStatus;
} {
  const { enabled = true } = options;
  const [status, setStatus] = useState<MapsStatus>(enabled ? 'ready' : 'idle');

  useEffect(() => {
    setStatus(enabled ? 'ready' : 'idle');
  }, [enabled]);

  return { status };
}

/** Test-only compat shim — the old loader kept a module singleton. No-op now. */
export function __resetMapsLoader(): void {
  // no-op
}
